import { GoalModel } from './goals';
import { AiUsageModel } from './imprvCoins';
import { UserModel } from './users';

enum MessageAttachmentType {
  TASK = 'TASK',
  TASK_ASSIGNMENT = 'TASK_ASSIGNMENT',
  GOAL = 'GOAL',
  GOAL_SUBSCRIPTION = 'GOAL_SUBSCRIPTION'
}

enum MessageReplyType {
  EXPLICIT = 'EXPLICIT',
  IMPLICIT = 'IMPLICIT'
}

enum MessageContextMenuVersion {
  CONSUMER = 'CONSUMER',
  PRODUCER = 'PRODUCER'
}

enum MessageRecommendationStatus {
  PENDING = 'PENDING',
  DECLINED = 'DECLINED',
  ACCEPTED = 'ACCEPTED',
  ACCEPTED_AFTER_EDIT = 'ACCEPTED_AFTER_EDIT'
}

enum ConversationSummaryType {
  MESSAGE = 'MESSAGE'
}

interface GetAttachmentsParamModel extends Record<string, any> {
  type?: string;
}

interface MessageAttachmentTaskResultModel {
  nodeEntityId: string;
  taskEntityId: string;
  title: string;
  type: string;
}

interface MessageAttachmentModel {
  id: number;
  createdAt: string;
  type: string;
  goal: GoalModel | null;
  task: MessageAttachmentTaskResultModel | null;
}

interface ConversationParticipantModel {
  id: number;
  name: string;
  pictureUrl: string;
  isMe: boolean;
  isMaster: boolean;
  isProducer: boolean;
  userId: number;
  chatbotEntityId?: string;
  type: string;
  user: UserModel;
}

interface MessageResponseRecommendationModel {
  id: number;
  content: string;
  status: MessageRecommendationStatus;
  inReplyToMessageId: number;
  conversationId: number;
  source: string;
  messageId?: number | null;
  reason: string;
}

interface MessageSenderFeedbackModel {
  acceptedForChatbot?: boolean | null;
}

interface MessageModel {
  id: number | null;
  cid: string | null;
  content: string | null;
  createdAt: string;
  attachments: null | MessageAttachmentModel[];
  conversationParticipant: ConversationParticipantModel;
  createdByChatbot: boolean;
  recipientFeedbacks: {
    chatbotBlockedByPresence: boolean | null;
    chatbotFailedReply: boolean | null;
    chatbotMarkedAsIrrelevant: boolean | null;
    chatbotReplyCancelled: boolean | null;
    estimatedTimeOfReply: string | null;
    humanMarkedAsIrrelevant: boolean | null;
    conversationParticipant: ConversationParticipantModel | null;
    replied: boolean | null;
    read: boolean | null;
  }[];
  inReplyToMessageId: number | null;
  aiUsage?: AiUsageModel | null;
  replyType?: MessageReplyType | null;
  inReplyToMessage?: {
    isDirectReply: boolean;
    id: number;
    content: string;
    conversationParticipant: ConversationParticipantModel;
    createdAt: string;
    attachments: null | MessageAttachmentModel[];
    createdByChatbot: boolean;
  };
  responseRecommendation?: MessageResponseRecommendationModel | null;
  hasPendingChatbotRecommendation?: boolean;
  senderFeedback?: MessageSenderFeedbackModel | null;
  // UI field
  acknowledged?: boolean;
}

interface CreatedMessageResponse {
  success: boolean;
  id: number;
  created_at: string;
}

interface CancelChatbotReplyResponse {
  conversationId: number;
  dispatched: boolean;
}

interface ConversationChatbotModel {
  entityId: string;
  name: string;
  botResponseThreshold: number;
  chatbotRepliesEnabled: boolean;
}

interface ConversationModel {
  id: number;
  status: string;
  participants: ConversationParticipantModel[];
  lastMessage: MessageModel | null;
  chatbot: ConversationChatbotModel | null;
}

interface ConversationSummaryModel {
  conversationId: number;
  type: ConversationSummaryType;
  text?: string | null;
  firstUnreadMessageTimestamp: string | null;
  sender: UserModel | null;
  senderIsChatbot: boolean;
  hasPendingRecommendation: boolean;
  context: {
    header: string;
    logo: string;
    goalSubscriptionId?: number;
    goalEntityId?: string;
    isMentor: boolean;
  };
  messagesCount?: number;
  unreadMessagesCount?: number;
  isDone?: boolean;
  openedByMe?: boolean;
  lastMessageTimestamp?: string | null;
}

interface InboxFilterUserOption {
  id: number;
  name: string;
  pictureUrl: string;
  isChatbot?: boolean;
}

interface InboxFilterGoalOption {
  goalEntityId: string;
  name: string;
  logo?: string;
}

interface InboxFilterOptionsModel {
  users: InboxFilterUserOption[];
  goals: InboxFilterGoalOption[];
}

interface ConversationSummariesResponseModel {
  summaries: ConversationSummaryModel[];
  size: number;
  total: number;
  notDoneTotal: number;
  nextCursor: string | null;
  filterOptions: InboxFilterOptionsModel | null;
}

interface ComposerCoinsGateState {
  isBalanceLoaded: boolean;
  // null until the ledger answers, so a non-number must be treated as unknown.
  remaining: number | null;
  usagesAvailable: boolean;
  isViewedByProducer: boolean;
  chatbotRepliesEnabled: boolean;
  hasDraft: boolean;
  hasUnsentMessages: boolean;
  isReplyInFlight: boolean;
  isGameActive: boolean;
}

export {
  ConversationSummaryType,
  MessageAttachmentType,
  MessageContextMenuVersion,
  MessageRecommendationStatus,
  MessageReplyType
};
export type {
  CancelChatbotReplyResponse,
  ComposerCoinsGateState,
  ConversationChatbotModel,
  ConversationModel,
  ConversationParticipantModel,
  ConversationSummariesResponseModel,
  ConversationSummaryModel,
  CreatedMessageResponse,
  GetAttachmentsParamModel,
  InboxFilterGoalOption,
  InboxFilterOptionsModel,
  InboxFilterUserOption,
  MessageAttachmentModel,
  MessageAttachmentTaskResultModel,
  MessageModel,
  MessageResponseRecommendationModel,
  MessageSenderFeedbackModel
};
